// Tiny synthesized audio feedback — no assets, just Web Audio oscillators.
// Subtle by design: soft chimes for good news, low horns for danger. The
// AudioContext unlocks on the first user gesture (browser autoplay policy),
// and a settings toggle (persisted) mutes everything.

import type { GameState } from './state'
import type { GameEvent } from '../shared/types'

let ctx: AudioContext | null = null
let muted = false
try {
  muted = localStorage.getItem('hexMuted') === '1'
} catch {
  /* private mode */
}

export function soundsMuted(): boolean {
  return muted
}

export function setMuted(m: boolean): void {
  muted = m
  try {
    localStorage.setItem('hexMuted', m ? '1' : '0')
  } catch {
    /* private mode */
  }
}

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return null
  ctx = new AC()
  return ctx
}

/** One soft note: freq in Hz, dur in seconds, delayed start in seconds. */
function tone(freq: number, dur: number, type: OscillatorType, gain: number, at = 0): void {
  if (muted) return
  const c = ensureCtx()
  if (!c || c.state !== 'running') return
  const t0 = c.currentTime + at
  const osc = c.createOscillator()
  const g = c.createGain()
  osc.type = type
  osc.frequency.value = freq
  g.gain.setValueAtTime(0, t0)
  g.gain.linearRampToValueAtTime(gain, t0 + 0.015)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  osc.connect(g)
  g.connect(c.destination)
  osc.start(t0)
  osc.stop(t0 + dur + 0.05)
}

const cues = {
  build: (): void => {
    tone(660, 0.22, 'triangle', 0.05)
    tone(880, 0.3, 'triangle', 0.045, 0.12)
  },
  danger: (): void => {
    tone(120, 0.35, 'sawtooth', 0.05)
    tone(98, 0.45, 'sawtooth', 0.05, 0.22)
  },
  death: (): void => {
    tone(330, 0.25, 'sine', 0.05)
    tone(220, 0.4, 'sine', 0.05, 0.16)
  },
  birth: (): void => {
    tone(523, 0.18, 'sine', 0.04)
    tone(659, 0.26, 'sine', 0.035, 0.1)
  },
  diplo: (): void => {
    tone(440, 0.2, 'triangle', 0.04)
    tone(554, 0.28, 'triangle', 0.035, 0.12)
  },
  error: (): void => {
    tone(150, 0.16, 'square', 0.035)
  },
}

/** Rate-limit per cue so an event burst doesn't stack into a wall of noise. */
const lastPlayed = new Map<string, number>()
function play(kind: keyof typeof cues): void {
  const now = performance.now()
  if (now - (lastPlayed.get(kind) ?? -1e9) < 1500) return
  lastPlayed.set(kind, now)
  cues[kind]()
}

export function initSound(st: GameState): void {
  // Unlock the context on the first gesture anywhere.
  const unlock = (): void => {
    const c = ensureCtx()
    if (c && c.state === 'suspended') void c.resume()
    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
  }
  window.addEventListener('pointerdown', unlock)
  window.addEventListener('keydown', unlock)

  st.on('events', (data: unknown) => {
    const list: GameEvent[] = Array.isArray(data) ? data : data ? [data as GameEvent] : []
    for (const ev of list) {
      if (ev.kind === 'combat' || ev.kind === 'raid') play('danger')
      else if (ev.kind === 'death') play('death')
      else if (ev.kind === 'build') play('build')
      else if (ev.kind === 'diplo') play('diplo')
      else if (ev.kind === 'info' && ev.msg.includes('was born')) play('birth')
    }
  })
  st.on('error', () => play('error'))
}
